import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { Package, AlertCircle, Save, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { usePluginList, usePluginStatus, usePluginConfig, usePluginConfigMutate, useReloadPlugins } from '@/hooks/useConfig';

export default function PluginsSection() {
  const { data: plugins, isLoading: pluginsLoading } = usePluginList();
  const { data: status, isLoading: statusLoading } = usePluginStatus();
  const { data: pluginConfig, isLoading: configLoading } = usePluginConfig();
  const updateConfig = usePluginConfigMutate();
  const reloadPlugins = useReloadPlugins();
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    enabled: true,
    packages: [],
    paths: []
  });
  const [newPackage, setNewPackage] = useState('');
  const [newPath, setNewPath] = useState('');
  
  const isLoading = pluginsLoading || statusLoading || configLoading;

  useEffect(() => {
    if (pluginConfig && !isEditing) {
      setFormData({
        enabled: pluginConfig.enabled ?? true,
        packages: pluginConfig.packages || [],
        paths: pluginConfig.paths || []
      });
    }
  }, [pluginConfig, isEditing]);

  const handleSave = async () => {
    await updateConfig.mutateAsync(formData);
    setIsEditing(false);
  };

  const handleAddPackage = () => {
    if (newPackage.trim()) {
      setFormData(prev => ({
        ...prev,
        packages: [...prev.packages, newPackage.trim()]
      }));
      setNewPackage('');
    }
  };

  const handleAddPath = () => {
    if (newPath.trim()) {
      setFormData(prev => ({
        ...prev,
        paths: [...prev.paths, newPath.trim()]
      }));
      setNewPath('');
    }
  };

  const handleRemovePackage = (index) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index)
    }));
  };

  const handleRemovePath = (index) => {
    setFormData(prev => ({
      ...prev,
      paths: prev.paths.filter((_, i) => i !== index)
    }));
  };

  const handleEnableToggle = () => {
    setFormData(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Plugins
        </CardTitle>
        <CardDescription>
          Configure and manage email analysis plugins
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner />
        ) : isEditing ? (
          // Edit mode - plugin configuration form
          <div className="space-y-4">
            {/* Enable/Disable toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Enable Plugins</p>
                <p className="text-sm text-muted-foreground">
                  When disabled, no external plugins will be loaded
                </p>
              </div>
              <Button
                variant={formData.enabled ? 'default' : 'secondary'}
                size="sm"
                onClick={handleEnableToggle}
              >
                {formData.enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>

            {/* NPM Packages */}
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">NPM Packages</p>
              <p className="text-sm text-muted-foreground mb-3">
                Enter npm package names (e.g., @boswell/plugin-price-analyzer)
              </p>
              <div className="space-y-2">
                {formData.packages.map((pkg, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm font-mono">{pkg}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemovePackage(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPackage}
                    onChange={(e) => setNewPackage(e.target.value)}
                    placeholder="Enter package name (e.g., @boswell/plugin-price-analyzer)"
                    className="flex-1 px-3 py-2 border rounded text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddPackage()}
                  />
                  <Button variant="outline" size="sm" onClick={handleAddPackage}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Directory Paths */}
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">Local Paths</p>
              <p className="text-sm text-muted-foreground mb-3">
                Enter directory paths containing plugins
              </p>
              <div className="space-y-2">
                {formData.paths.map((path, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm font-mono">{path}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemovePath(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPath}
                    onChange={(e) => setNewPath(e.target.value)}
                    placeholder="Enter path (e.g., ./plugins)"
                    className="flex-1 px-3 py-2 border rounded text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddPath()}
                  />
                  <Button variant="outline" size="sm" onClick={handleAddPath}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={updateConfig.isPending}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    enabled: pluginConfig?.enabled ?? true,
                    packages: pluginConfig?.packages || [],
                    paths: pluginConfig?.paths || []
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          // Display mode
          <div className="space-y-4">
            {/* Configuration summary */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Plugin Status</p>
                <p className="text-sm text-muted-foreground">
                  {status?.enabled ? 'Plugins are enabled' : 'Plugins are disabled'}
                </p>
              </div>
              <div className="flex gap-2">
                <Badge variant={status?.enabled ? 'success' : 'secondary'}>
                  {status?.totalPlugins || 0} loaded
                </Badge>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  Configure
                </Button>
              </div>
            </div>

            {/* Plugin list */}
            <div>
              <p className="font-medium mb-2">Loaded Plugins</p>
              {statusLoading ? (
                <LoadingSpinner />
              ) : !status?.plugins || status.plugins.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No plugins loaded
                </div>
              ) : (
                <div className="space-y-3">
                  {status.plugins.map((plugin) => (
                    <div key={plugin.name} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            {plugin.name}
                            <Badge variant={plugin.enabled ? 'success' : 'secondary'} className="text-xs">
                              v{plugin.version}
                            </Badge>
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            Source: {plugin.source}
                          </p>
                        </div>
                      </div>
                      {plugin.hasError && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600">
                          <AlertCircle className="h-4 w-4 inline mr-1" />
                          {plugin.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}